<template>
  <ProgressCircular v-if="loading" />
  <form v-else novalidate @submit.prevent="submit()">
    <v-row no-gutters>
      <v-col cols="8">
        <v-card max-width="1200px" flat color="transparent">
          <v-card-title>
            <FormHeader
              :loading="loading"
              :link="'page-categories'"
              :title="data.title ? `${data.title} категория` : `Создать категорию`"
            />
          </v-card-title>
          <v-card-text>
            <v-container>
              <v-row>
                <v-col cols="12">
                  <span>Заголовок * (<TranslationIcon />)</span>
                  <v-text-field
                    outlined
                    autofocus
                    id="title"
                    :error-messages="validationErrors.title"
                    :disabled="loading"
                    :loading="loading"
                    v-model="fields.title"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <v-text-field
                    outlined
                    :label="'Сортировка'"
                    :disabled="loading"
                    :loading="loading"
                    :error-messages="validationErrors.sortOrder"
                    v-model.trim="fields.sortOrder"
                  />
                </v-col>
              </v-row>
              <v-row v-if="!isNew">
                <v-col cols="12">
                  <v-text-field
                    outlined
                    id="slug"
                    :label="'Имя *'"
                    :disabled="loading"
                    :loading="loading"
                    v-model.trim="fields.slug"
                    :error-messages="validationErrors.slug"
                    @input="$v.fields.slug.$touch"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <MediaFilePicker
                    :label="'Изображение'"
                    :multiple="false"
                    :file-type="$options.mediaTypesByKeys.IMAGE"
                    :value="fields.imageId"
                    @change="fields.imageId = $event"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <SeoSettings
                    :data="data"
                    :loading="loading"
                    @on-input-seo="fields = Object.assign({}, fields, $event)"
                  />
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="4">
        <ManageSidePanel
          :is-new="!data.id"
          :created-at="data.createdAt"
          :updated-at="data.updatedAt"
          d-at="data.publishedAt"
          :author="data.author"
          :is-loading="loading || $apollo.queries.eventAttributeKeys.loading"
          :language="language"
          :translations="fields.translations"
          :entity-id="data.id"
          :entity-type="'eventCategory'"
          @delete="$emit('delete-category')"
          @cancel="$router.push({ name: 'page-categories' })"
          @update-translations="Object.assign(fields.translations, $event)"
          @language-update="$emit('language-update', $event)"
        >
          <template #body>
            <v-card-text v-if="!!eventAttributeKeys">
              <v-card-subtitle class="text-h6 mb-2">{{
                $t('page-new-event-category.form.attrList')
              }}</v-card-subtitle>
              <v-chip-group
                :value="
                  fields.attributeKeys.map(attr =>
                    eventAttributeKeys.findIndex(i => i.name === attr.name)
                  ) || []
                "
                column
                multiple
                @change="
                  fields.attributeKeys =
                    $event.map(i => ({ name: eventAttributeKeys[i].name })) || []
                "
              >
                <v-chip
                  filter
                  outlined
                  v-for="attributeKeys in eventAttributeKeys"
                  :key="attributeKeys.name"
                >
                  {{ attributeKeys.displayName }}
                </v-chip>
              </v-chip-group>
            </v-card-text>
          </template>
        </ManageSidePanel>
      </v-col>
    </v-row>
  </form>
</template>
<script>
import validationRules from './validation-rules.js'
import { mediaTypesByKeys } from '@/configs/mediaFileTypes'

import validationTranslations from '@/mixins/translations/validationTranslations.mix.js'
import prepareTranslations from '@/mixins/translations/prepareTranslations.mix'

import FormAbstract from '@/components/abstract/form'
import ManageSidePanel from '@/components/manage-side-panel'
import MediaFilePicker from '@/components/media-file-picker'
import TranslationIcon from '@/components/translation-icon/translationIcon'
import FormHeader from '@/components/form-header'
import SeoSettings from '@/components/seo-settings'
import ProgressCircular from '@/components/progress-circular'

import eventAttributeKeys from '@/services/gql/types/EventAttributeKeys/queries/eventAttributeKeys.gql'
import { mixForm } from '@/utils/form.mix.js'

export default {
  extends: FormAbstract,
  mixins: [validationTranslations, prepareTranslations, mixForm],
  name: 'FormCategory',
  validationRules,
  mediaTypesByKeys: mediaTypesByKeys(),
  components: {
    TranslationIcon,
    ManageSidePanel,
    MediaFilePicker,
    FormHeader,
    SeoSettings,
    ProgressCircular
  },
  props: {
    data: {
      type: Object,
      default: null
    },
    isNew: {
      type: Boolean,
      default: true
    },
    language: {
      type: String,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      fields: {
        title: null,
        slug: undefined,
        imageId: null,
        sortOrder: null,
        seoMetaTitle: null,
        seoMetaDescription: null,
        seoDisallowIndex: false,
        seoMetaKeywords: null,
        seoRelCanonical: null,
        seoOgImageId: null,
        attributeKeys: [],
        translations: { ru: { isEnabled: true } }
      },
      eventAttributeKeys: []
    }
  },
  watch: {
    data (val) {
      this.populate(val)
      this.fields.attributeKeys =
        val?.attributeKeys.map(attr => ({ name: attr.name })) || []
      this.fields.imageId = val?.image?.id || null
    }
  },
  computed: {
    validationRulesVars () {
      return { id: this.data?.id, ctx: this, isNew: this.isNew }
    }
  },
  methods: {
    async submit () {
      const isValid = await this.validate()
      if (!isValid) {
        this.mixScrollToError(this.$v)
        return
      }
      if (!this.mixIsValidTranslations(this.fields.translations)) {
        this.$errorMsg('Не выбран ни один язык для контента!')
        return
      }
      const payload = {
        ...this.composeFormData(this.fields)
      }

      this.mixPrepareTranslations(payload)
      payload.sortOrder = Number(payload.sortOrder)
      this.$emit('submit', payload)
    }
  },
  apollo: {
    eventAttributeKeys: {
      query: eventAttributeKeys,
      fetchPolicy: 'network-only',
      variables () {
        return {
          where: {},
          orderBy: 'name_ASC'
        }
      },
      result (res) {
        this.eventAttributeKeys = res.data.eventAttributeKeys.list
      }
    }
  }
}
</script>
